import { useEffect, useState } from "react";
import { useUserContext } from "../../../shared/contexts/UserContext";
import { useDMQuery } from "../../../utils";
import { getStartEndDate } from "../ReportUtil";
import { SchoolData } from "../ReportTypes";
import StudentsIcon from "../../../shared/icons/StudentsIcon";
import AssignmentsIcon from "../../../shared/icons/AssignmentsIcon";
import WarningCircleIcon from "../../../shared/icons/WarningCircleIcon";
import ClockIcon from "../../../shared/icons/ClockIcon";
import LoadingSkeloton from "../../utils/LoadingSkeleton";
import UsageStatItem from "./UsageStatItem";

const UsageQuickStats = ({ schoolData }: { schoolData: SchoolData[] }) => {
  const userContext = useUserContext();
  const token = userContext.getJWT();
  const { yesterday, yearToDate } = getStartEndDate();
  const [problemsSolved, setProblemsSolved] = useState<number>(0);
  const [hoursSpent, setHoursSpent] = useState<number>(0);
  const [numStudents, setNumStudents] = useState<number>(0);
  const [numAssignments, setNumAssignments] = useState<number>(0);

  // CC - There is no good reason we should be computing this data on the client side.
  // If we get around to it we should optimize this and compute this on the server side. - Possibly even caching it
  // For a large district this is a pretty phenomenal amount of data to be sending over the wire.
  const { data } = useDMQuery({
    cacheKey: [`usage-quick-stats`],
    path: `/admin_new/data/usage`,
    params: {
      start:
        yearToDate?.getTime() &&
        Math.floor(yearToDate?.getTime() / 1000).toString(),
      end:
        yesterday?.getTime() &&
        Math.floor(yesterday?.getTime() / 1000).toString(),
      district: true,
    },
    method: "get",
    additionalHeaders: {
      Authorization: `Bearer ${token}`,
    },
    queryOptions: {
      enabled: true,
      staleTime: 1000 * 60 * 15,
    },
  });

  useEffect(() => {
    const studentSums = schoolData.reduce(
      (acc: { studentCount: number; assignmentCount: number }, school) => {
        return {
          studentCount: acc.studentCount + school.studentCount,
          assignmentCount: acc.assignmentCount + school.assignmentCount,
        };
      },
      { studentCount: 0, assignmentCount: 0 }
    );

    setNumAssignments(studentSums.assignmentCount);
    setNumStudents(studentSums.studentCount);
  }, [schoolData]);

  useEffect(() => {
    if (data && data.data) {
      const usageSums = aggregateUsageData(Object.values(data.data));
      setHoursSpent(usageSums.t / 60);
      setProblemsSolved(usageSums.n);
    }
  }, [data]);

  return (
    <div className="flex justify-between space-x-[24px] px-6">
      {schoolData ? (
        <>
          <UsageStatItem
            icon={<StudentsIcon />}
            name={"Total Students"}
            data={numStudents}
          />
          <UsageStatItem
            key={"Total Assignments"}
            icon={<AssignmentsIcon />}
            name={"Total Assignments"}
            data={numAssignments}
          />
        </>
      ) : (
        <>
          <LoadingSkeloton />
          <LoadingSkeloton />
        </>
      )}

      {data ? (
        <>
          <UsageStatItem
            key={2}
            icon={<WarningCircleIcon />}
            name={"Problems Solved"}
            data={problemsSolved}
          />
          <UsageStatItem
            key={3}
            icon={<ClockIcon />}
            name={"Hours Spent on Site"}
            data={hoursSpent}
          />
        </>
      ) : (
        <>
          <LoadingSkeloton />
          <LoadingSkeloton />
        </>
      )}
    </div>
  );
};

export default UsageQuickStats;

const aggregateUsageData = (data: Array<{ n: number; t: number }>) =>
  data.reduce(
    (valA: { n: number; t: number }, valB: { n: number; t: number }) => ({
      n: valA.n + valB.n,
      t: valA.t + valB.t,
    }),
    { n: 0, t: 0 }
  );
